<template>
    <div class="lg:grid lg:grid-cols-12 lg:gap-x-5">
        <ControlsMenuComponent />

        <div class="space-y-6 sm:px-6 lg:col-span-9 md:max-w-xl mx-auto lg:px-0" v-if="!profile">
            <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
                <div class="space-y-6 bg-white px-4 py-6 sm:p-6" v-if="!isCreatingProfile">
                    <div>
                        <h3 class="text-base font-semibold text-gray-900">{{ $t('profile.title') }}</h3>
                        <p class="mt-2 text-sm text-gray-500">{{ $t('profile.creating_tip') }}</p>
                        <p class="mt-2 text-sm text-gray-500">{{ $t('profile.creating_tip_2') }}</p>

                        <div class="w-full flex justify-center mt-5">
                            <button type="button" @click.prevent="isCreatingProfile = true" class="relative inline-flex items-center gap-x-1.5 mr-3 rounded-md bg-royal-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-royal-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royal-blue-500 cursor-pointer">Створити профіль</button>
                        </div>
                    </div>
                </div>
                <form @submit.prevent="submit" class="space-y-6 bg-white px-4 py-6 sm:p-6" v-if="isCreatingProfile">
                    <div>
                        <h3 class="text-base font-semibold text-gray-900">{{ $t('profile.creating_title') }}</h3>
                        <p class="mt-2 text-sm text-gray-500">{{ $t('profile.creating_text') }}</p>

                        <div class="w-full mt-5">
                            <label for="title" class="block text-sm/6 font-medium text-gray-900">{{ $t('profile.name') }}</label>
                            <div class="mt-2 grid grid-cols-1">
                                <input
                                    id="title"
                                    name="title"
                                    v-model="form.title"
                                    :placeholder="user.name"
                                    type="text"
                                    autocomplete="title"
                                    :class="{
                                            'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('title')
                                        }" />
                                <ExclamationCircleIcon v-if="hasError('title')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                            </div>
                            <p class="mt-2 text-sm text-red-600" id="title-error" v-if="hasError('title')">{{ getError('title') }}</p>
                        </div>

                        <div class="w-full flex justify-center mt-5">
                            <button type="submit" class="relative inline-flex items-center gap-x-1.5 mr-3 rounded-md bg-royal-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-royal-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royal-blue-500 cursor-pointer">{{ $t('profile.continue') }}</button>
                        </div>
                    </div>
                </form>
            </div>
        </div>

        <div class="space-y-6 sm:px-6 lg:col-span-9 lg:px-0" v-if="profile && user">
            <ShareProfileModalComponent
                v-if="profile && profile.public_url"
                :open="showShareModal"
                :url="profile.public_url"
                :qr-url="publicUrl(`qr-code/share?size=500&url=${profile.public_url}`)"
                :name="profile.title"
                @close="showShareModal = false"
            />

            <div class="relative sm:border-b border-gray-200 pb-0 sm:pb-0">
                <div class="mt-4">
                    <div class="sm:block">
                        <nav class="-mb-px flex space-x-4">
                            <a v-for="tab in tabs" :key="tab.name" href="#"
                               @click.prevent="activeTab = tab.id"
                               :class="[
                                   tab.id == activeTab
                                   ? 'border-blue-600 text-blue-700'
                                   : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',

                                   'border-b-2 px-1 pb-4 text-sm font-medium whitespace-nowrap'
                               ]" :aria-current="tab.id == activeTab ? 'page' : undefined">
                                <span>{{ tab.name }}</span>
                                <span v-if="tab.id == 'prices'" class="ms-2 inline-flex items-center rounded-full bg-gray-300 px-1.5 py-0.5 text-xs font-medium text-gray-600 inset-ring inset-ring-gray-500/10">New</span>
                            </a>
                        </nav>
                    </div>
                </div>
                <div class="md:flex md:items-center md:justify-between">
                    <div v-if="profile" class="w-full sm:w-auto mt-3 inline-flex gap-2 md:absolute md:top-0 md:right-0 md:mt-0">
                        <a :href="profile.public_url" target="_blank" class="flex-1 sm:flex-none inline-flex justify-center items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50">{{ $t('profile.view_profile') }}</a>
                        <button @click.prevent="showShareModal = true" class="flex-1 sm:flex-none inline-flex justify-center items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 cursor-pointer">{{ $t('profile.share_profile') }}</button>
                    </div>
                </div>
            </div>

            <form v-if="activeTab == 'main'" @submit.prevent="updateProfile" enctype="multipart/form-data">
                <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
                    <div class="space-y-6 bg-white px-4 py-6 sm:p-6">
                        <div>
                            <h3 class="text-base font-semibold text-gray-900">{{ $t('profile.general_title') }}</h3>
                            <p class="mt-1 text-sm text-gray-500">{{ $t('profile.view_profile') }}</p>
                        </div>

                        <div class="grid grid-cols-6 gap-6" v-if="profile">
                            <div class="col-span-6 grid grid-cols-4 gap-6">
                                <div class="col-span-4 sm:col-span-2">
                                    <label for="avatar" class="block text-sm/6 font-medium text-gray-900">{{ $t('profile.avatar') }}</label>
                                    <AvatarComponent class="mt-2" :current_url="form.avatar_url" />
                                </div>

                                <div class="col-span-4 sm:col-span-2">
                                    <div class="inline-flex items-center w-full justify-between">
                                        <label for="cover-photo" class="block text-sm/6 font-medium text-gray-900">{{ $t('profile.cover') }}</label>
                                        <button v-if="form?.cover?.uploaded" type="button" @click.prevent="deleteCover" class="text-xs text-gray-500 hover:text-red-600 cursor-pointer hover:underline">
                                            {{ $t('profile.delete_cover') }}
                                        </button>
                                    </div>
                                    <CoverUploaderComponent
                                        :height="100"
                                        :position="form.cover.position"
                                        :cover_url="(form.cover.uploaded && form.cover.url) ? form.cover.url : undefined"
                                        :preview_url="cover.preview_url || undefined"
                                        :is_file_selected="cover.file !== null"
                                        :is_file_uploading="cover.is_uploading"
                                        v-if="form.cover"
                                        @positionUpdated="coverPositionsUpdated"
                                        @coverFileSelected="selectCoverFile"
                                    />
                                </div>
                            </div>

                            <div class="col-span-6">
                                <label for="title" class="block text-sm/6 font-medium text-gray-900">{{ $t('profile.name') }}</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <input
                                        id="title"
                                        name="title"
                                        v-model="form.title"
                                        type="text"
                                        autocomplete="title"
                                        :class="{
                                            'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('title')
                                        }" />
                                    <ExclamationCircleIcon v-if="hasError('title')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                </div>
                                <p class="mt-2 text-sm text-red-600" id="title-error" v-if="hasError('title')">{{ getError('title') }}</p>
                            </div>

                            <div class="col-span-6">
                                <label for="username" class="block text-sm/6 font-medium text-gray-900">{{ $t('profile.address') }}</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <input
                                        type="text"
                                        name="username"
                                        id="username"
                                        v-model="form.username"
                                        :class="{
                                            'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('username')
                                        }" placeholder="janesmith" />
                                    <ExclamationCircleIcon v-if="hasError('username')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                </div>
                                <p class="mt-2 text-sm text-red-600" id="name-error" v-if="hasError('username')">{{ getError('username') }}</p>
                                <p class="mt-1 text-sm/6 text-gray-400">
                                    <span v-if="form.username">{{ $t('profile.your_address') }} <span class="font-medium text-gray-600">{{ form.username }}.gallera.io</span></span>
                                    <span v-else></span>
                                </p>
                                <p class="mt-1 text-sm/6 text-gray-400">{{ $t('profile.address_tip') }}</p>
                            </div>

                            <div class="col-span-full">
                                <label for="description" class="block text-sm/6 font-medium text-gray-900">{{ $t('profile.description') }}</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <UserDescriptionEditor
                                        v-model="form.description"
                                        :class="{ 'is-invalid': hasError('description') }"
                                    />
                                    <ExclamationCircleIcon v-if="hasError('description')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                </div>
                                <p class="mt-2 text-sm text-red-600" id="description-error" v-if="hasError('description')">{{ getError('description') }}</p>
                                <p class="mt-1 text-sm/6 text-gray-400">{{ $t('profile.description_tip') }}</p>
                            </div>

                            <div class="col-span-full">
                                <label for="prices_block_title" class="block text-sm/6 font-medium text-gray-900">{{ $t('profile.prices_block_title_label') }}</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <input
                                        id="prices_block_title"
                                        name="prices_block_title"
                                        v-model="form.prices_block_title"
                                        type="text"
                                        autocomplete="off"
                                        :placeholder="$t('profile.prices_title')"
                                        class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6" />
                                </div>
                                <p class="mt-1 text-sm/6 text-gray-400">{{ $t('profile.prices_block_title_tip') }}</p>
                            </div>

                            <div class="col-span-full">
                                <label for="locale" class="block text-sm/6 font-medium text-gray-900">{{ $t('gallery_settings.general.language') }}</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <select
                                        id="locale"
                                        name="locale"
                                        v-model="form.locale"
                                        :class="{
                                            'col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('locale')
                                        }">
                                        <option value="uk">{{ $t('gallery_settings.general.languages.ukrainian') }}</option>
                                        <option value="en">{{ $t('gallery_settings.general.languages.english') }}</option>
                                        <option value="cz">{{ $t('gallery_settings.general.languages.czech') }}</option>
                                        <option value="de">{{ $t('gallery_settings.general.languages.german') }}</option>
                                        <option value="es">{{ $t('gallery_settings.general.languages.spanish') }}</option>
                                        <option value="fr">{{ $t('gallery_settings.general.languages.french') }}</option>
                                        <option value="it">{{ $t('gallery_settings.general.languages.italian') }}</option>
                                        <option value="pl">{{ $t('gallery_settings.general.languages.polish') }}</option>
                                    </select>
                                    <ChevronDownIcon class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" aria-hidden="true" />
                                    <ExclamationCircleIcon v-if="hasError('locale')" class="pointer-events-none col-start-1 row-start-1 mr-8 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                </div>
                                <p class="mt-2 text-sm text-red-600" id="locale-error" v-if="hasError('locale')">{{ getError('locale') }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 px-4 py-3 text-right sm:px-6">
                        <button type="submit" class="inline-flex justify-center rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-600 focus:outline-hidden cursor-pointer">{{ $t('profile.save') }}</button>
                    </div>
                </div>
            </form>

            <form v-if="activeTab == 'contacts'" @submit.prevent="updateProfile" enctype="multipart/form-data">
                <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
                    <div class="space-y-6 bg-white px-4 py-6 sm:p-6">
                        <div>
                            <h3 class="text-base font-semibold text-gray-900">{{ $t('profile.contacts') }}</h3>
                            <p class="mt-1 text-sm text-gray-500">{{ $t('profile.contacts_tip') }} <span class="text-gray-400">{{ $t('profile.contacts_tip_2') }}</span></p>
                        </div>

                        <div class="grid grid-cols-6 gap-6">
                            <div class="col-span-6 sm:col-span-6">
                                <label for="phone_number" class="block text-sm/6 font-medium text-gray-900">{{ $t('profile.phone') }}</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <input
                                        id="phone_number"
                                        name="phone_number"
                                        v-model="form.phone_number"
                                        type="text"
                                        autocomplete="phone"
                                        :class="{
                                            'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('phone_number')
                                        }" />
                                    <ExclamationCircleIcon v-if="hasError('phone_number')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                </div>
                                <p class="mt-2 text-sm text-red-600" id="phone_number-error" v-if="hasError('phone_number')">{{ getError('phone_number') }}</p>
                            </div>

                            <div class="col-span-6 sm:col-span-6">
                                <label for="email" class="block text-sm/6 font-medium text-gray-900">{{ $t('profile.email') }}</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <input
                                        id="email"
                                        name="email"
                                        v-model="form.email"
                                        type="email"
                                        autocomplete="email"
                                        :class="{
                                            'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('email')
                                        }" />
                                    <ExclamationCircleIcon v-if="hasError('email')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                </div>
                                <p class="mt-2 text-sm text-red-600" id="email-error" v-if="hasError('email')">{{ getError('email') }}</p>
                                <p class="mt-1 text-sm/6 text-gray-400">{{ $t('profile.email_tip') }}</p>
                            </div>

                            <div class="col-span-6 sm:col-span-6">
                                <label for="location" class="block text-sm/6 font-medium text-gray-900">{{ $t('profile.location') }}</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <input
                                        id="location"
                                        name="location"
                                        v-model="form.location"
                                        type="text"
                                        autocomplete="location"
                                        placeholder="Київ, Україна"
                                        :class="{
                                            'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('location')
                                        }" />
                                    <ExclamationCircleIcon v-if="hasError('location')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                </div>
                                <p class="mt-2 text-sm text-red-600" id="location-error" v-if="hasError('location')">{{ getError('location') }}</p>
                            </div>

                            <div class="col-span-6 sm:col-span-6">
                                <label for="instagram_url" class="block text-sm/6 font-medium text-gray-900">Instagram</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <input
                                        id="instagram_url"
                                        name="instagram_url"
                                        v-model="form.instagram_url"
                                        type="text"
                                        autocomplete="instagram_url"
                                        :class="{
                                            'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('instagram_url')
                                        }" />
                                    <ExclamationCircleIcon v-if="hasError('instagram_url')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                </div>
                                <p class="mt-2 text-sm text-red-600" id="instagram_url-error" v-if="hasError('instagram_url')">{{ getError('instagram_url') }}</p>
                            </div>

                            <div class="col-span-6 sm:col-span-6">
                                <label for="facebook_url" class="block text-sm/6 font-medium text-gray-900">Facebook</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <input
                                        id="facebook_url"
                                        name="facebook_url"
                                        v-model="form.facebook_url"
                                        type="text"
                                        autocomplete="facebook_url"
                                        :class="{
                                            'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('facebook_url')
                                        }" />
                                    <ExclamationCircleIcon v-if="hasError('facebook_url')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                </div>
                                <p class="mt-2 text-sm text-red-600" id="facebook_url-error" v-if="hasError('facebook_url')">{{ getError('facebook_url') }}</p>
                            </div>

                            <div class="col-span-6 sm:col-span-6">
                                <label for="telegram_url" class="block text-sm/6 font-medium text-gray-900">Telegram</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <input
                                        id="telegram_url"
                                        name="telegram_url"
                                        v-model="form.telegram_url"
                                        type="text"
                                        autocomplete="telegram_url"
                                        :class="{
                                            'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('telegram_url')
                                        }" />
                                    <ExclamationCircleIcon v-if="hasError('telegram_url')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                </div>
                                <p class="mt-2 text-sm text-red-600" id="telegram_url-error" v-if="hasError('telegram_url')">{{ getError('telegram_url') }}</p>
                            </div>

                            <div class="col-span-6 sm:col-span-6">
                                <label for="website_url" class="block text-sm/6 font-medium text-gray-900">{{ $t('profile.website') }}</label>
                                <div class="mt-2 grid grid-cols-1">
                                    <input
                                        id="website_url"
                                        name="website_url"
                                        v-model="form.website_url"
                                        type="text"
                                        autocomplete="website_url"
                                        :placeholder="user ? `https://${user.username}.gallera.io` : 'https://username.gallera.io'"
                                        :class="{
                                            'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6': true,
                                            'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('website_url')
                                        }" />
                                    <ExclamationCircleIcon v-if="hasError('website_url')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                </div>
                                <p class="mt-2 text-sm text-red-600" id="website_url-error" v-if="hasError('website_url')">{{ getError('website_url') }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 px-4 py-3 text-right sm:px-6">
                        <button type="submit" class="inline-flex justify-center rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-600 focus:outline-hidden cursor-pointer">Зберегти</button>
                    </div>
                </div>
            </form>

            <div v-if="activeTab == 'portfolio'">
                <PortfolioSection
                    :profile="profile"
                />
            </div>

            <div v-if="activeTab == 'prices'">
                <PricesSection />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapActions, mapGetters} from "vuex";

import ControlsMenuComponent from "@/components/Users/ControlsMenuComponent.vue";
import PortfolioSection from "@/components/Users/Profile/PortfolioSection.vue";
import PricesSection from "@/components/Users/Profile/PricesSection.vue";
import ShareProfileModalComponent from "@/components/Users/Profile/Modals/ShareProfileModalComponent.vue";

import {UserProfile} from "@/types/UserType";
import {validationMethods} from "@/_helpers/validation";

import {ResponseError} from "@/types";
import CoverUploaderComponent from "@/components/Galleries/CoverUploaderComponent.vue";
import {updateCover, updateProfile} from "@/data/requests/profiles";
import {Gallery} from "@/types/GalleryType";
import AvatarComponent from "@/components/Users/AvatarComponent.vue";
import {ExclamationCircleIcon, ChevronDownIcon} from "@heroicons/vue/16/solid";
import UserDescriptionEditor from "@/components/Users/Profile/UserDescriptionEditor.vue";

export default defineComponent({
    name: "UserProfilePage",

    components: {
        ControlsMenuComponent,
        ExclamationCircleIcon,
        ChevronDownIcon,
        AvatarComponent,
        CoverUploaderComponent,
        UserDescriptionEditor,
        PortfolioSection,
        PricesSection,
        ShareProfileModalComponent
    },

    data() {
        return {
            form: {} as UserProfile,

            cover: {
                file: null as File | null,
                preview_url: null as string | null,
                is_uploading: false,
            },

            isCreatingProfile: false,

            errors: {} as { [key: string]: string[] },

            addItemForm: {
                gallery: null as Gallery | null,
                file: null as File | null,
            },

            drag: false,

            activeTab: "main",

            showShareModal: false,
        }
    },

    computed: {
        ...mapGetters({
            user: "user/user",
            profile: "profile/profile",
        }),
        tabs(): { name: string; href: string; id: string }[] {
            const pricesName = (this.profile && this.profile.prices_block_title && String(this.profile.prices_block_title).trim() !== '')
                ? String(this.profile.prices_block_title).trim()
                : this.$t('profile.navigation.prices');
            return [
                { name: this.$t('profile.navigation.general'), href: '#', id: 'main' },
                { name: this.$t('profile.navigation.contacts'), href: '#', id: 'contacts' },
                { name: this.$t('profile.navigation.portfolio'), href: '#', id: 'portfolio' },
                { name: pricesName, href: '#', id: 'prices' },
            ];
        },
    },

    watch: {
        'form.title': function () {
            delete this.errors['title'];
        },
        'form.username': function () {
            delete this.errors['username'];
        },
    },

    mounted() {
        this.fetchProfileData();
    },

    methods: {
        ...mapActions({
            loadProfile: "profile/loadProfile",
            createProfile: "profile/createProfile",

            successAlert: "alerts/success",
            errorAlert: "alerts/error",
        }),

        ...validationMethods(),

        fetchProfileData() {
            this.loadProfile()
                .then((profile) => {
                    this.form = profile;
                }).catch(({data, type}) => {
                if(data.message && type != ResponseError.NotFound) {
                    this.errorAlert({
                        message: this.$t('profile.fetch_error'),
                        timeout: 30
                    })
                }
            });
        },

        submit() {
            if(this.profile) {
                this.updateProfile();

                return;
            }

            this.createProfile({
                title: this.form.title,
            })
                .then(() => {
                    this.fetchProfileData();
                })
                .catch(({type, data}) => {
                    if(type == ResponseError.Validation) {
                        this.errors = data.data;

                        this.errorAlert({
                            message: this.$t('profile.title_error'),
                        });
                    } else {
                        this.errorAlert({
                            message: data.message,
                        });
                    }
                });
        },

        updateProfile() {
            updateProfile(this.form, ({status}) => {
                if(status === "success") {
                    this.successAlert({
                        message: this.$t('profile.success_saved'),
                    });
                }

                this.loadProfile()
                    .then((profile) => {
                        console.log('fetch profile', profile);

                        this.form = profile;
                    });
            }, (type, data) => {
                if(type == ResponseError.Validation) {
                    this.errors = data.data;

                    this.errorAlert({
                        message: this.$t('profile.validation_error'),
                    });
                } else {
                    this.errorAlert({
                        message: data.message,
                    });
                }
            })
        },

        selectCoverFile(file: File | null) {
            delete this.errors['cover'];

            this.cover.file = file;

            if(this.cover.file) {
                this.cover.preview_url = URL.createObjectURL(this.cover.file);

                this.cover.is_uploading = true;

                updateCover(this.cover.file, (data) => {
                    this.form.cover.url = data.data.url;
                    this.form.cover.uploaded = true;
                    this.cover.file = null;
                    this.cover.preview_url = null;

                    this.cover.is_uploading = false;
                }, (type, data) => {
                    this.cover.file = null;
                    this.cover.preview_url = null;

                    if (type == ResponseError.Validation) {
                        this.errors = data.data;
                    }

                    this.errorAlert({
                        message: this.$t('profile.cover_error'),
                    });

                    this.cover.is_uploading = false;
                });
            }
        },

        coverPositionsUpdated(positions: {x: number, y: number}) {
            this.form.cover.position = positions;
        },

        deleteCover() {
            if (this.form.cover.uploaded) {
                // Викликаємо API для видалення обкладинки
                this.$store.dispatch('profile/deleteCover')
                    .then(() => {
                        this.successAlert({
                            message: this.$t('profile.cover_deleted'),
                        });
                        
                        // Оновлюємо дані профілю
                        this.loadProfile()
                            .then((profile) => {
                                this.form = profile;
                            });
                    })
                    .catch(() => {
                        this.errorAlert({
                            message: this.$t('profile.cover_delete_error'),
                        });
                    });
            }
        },

        publicUrl(url: string): string {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
    }
})
</script>

<style lang="scss" scoped>

</style>